import { z } from 'zod'

import { api, ApiError } from '@/shared/api'

import { GOFTINO_USER_ID } from '../lib/goftino'

/**
 * `GET /support/chat/session` (§19): the Goftino id bound to the caller's
 * account, and the profile core lets the widget show an operator.
 *
 * **`user_id` is the widget's own id, or `null`.** Goftino accepts only ids
 * its own widget generated, so core never mints one: it binds the first id
 * one of the user's devices reports, and answers `null` until then — and
 * again after a revocation or a retirement.
 *
 * **`user_id` is a bearer secret.** Whoever passes it to `setUserId` reads
 * that conversation. It is never logged, never put in a URL of this app's, in
 * route state or in the TanStack cache, and never stored by this app: a query
 * would keep it in the cache for `gcTime`, so this is a plain call, made on
 * every open. It is checked against Goftino's shape here, before it can reach
 * a script URL; a mismatch fails the parse, and Zod's issue carries no input.
 *
 * **`profile` goes to `Goftino.setUser` as sent.** Core decides what leaves
 * for Goftino, so every key it sends is kept and none is added; each is text.
 * `name` is absent before KYC.
 *
 * While the chat is off the route answers `404` `not_found` with
 * `detail.reason = "support_chat_disabled"`.
 */
export const chatSessionResponse = z.object({
  provider: z.literal('goftino'),
  widget_id: z.string().min(1),
  user_id: z.string().regex(GOFTINO_USER_ID).nullable(),
  profile: z.record(z.string(), z.string()),
})

export type ChatSession = z.infer<typeof chatSessionResponse>

export function fetchChatSession(signal?: AbortSignal): Promise<ChatSession> {
  return api.get('/support/chat/session', { schema: chatSessionResponse, signal })
}

const boundIdentity = z.object({ user_id: z.string().regex(GOFTINO_USER_ID) })

/**
 * `PUT /support/chat/identity`: binds the id the widget generated, for an
 * account with none. First write wins, so the answer is the account's id,
 * which is not always the one sent:
 *
 *  - `201`: bound, the id sent;
 *  - `200`: the account already had one, and another device bound it first
 *    when it differs from the id sent;
 *  - `409` `chat_identity_taken`: the id is in use — someone else's, or this
 *    account's own retired one — and is discarded;
 *  - `429` with `retry_after_seconds`, `404` `support_chat_disabled`, `401`
 *    for a session revoked mid-request, and `422` for anything that is not
 *    64 alphanumerics, which this app never sends.
 *
 * `no-store`, and the id is the same bearer secret as the session's.
 */
export async function bindChatIdentity(
  goftinoUserId: string,
  signal?: AbortSignal,
): Promise<string> {
  const bound = await api.put('/support/chat/identity', {
    body: { goftino_user_id: goftinoUserId },
    schema: boundIdentity,
    signal,
  })
  return bound.user_id
}

/**
 * `DELETE /support/chat/identity`: retires the account's live identity, for
 * an id Goftino refuses twice. `204`, also when nothing is live, and the next
 * session answers `null`. Shares the bind's `429`, `404` and `401`.
 */
export function retireChatIdentity(signal?: AbortSignal): Promise<void> {
  return api.delete('/support/chat/identity', { signal })
}

/** The bind's `409`: the id belongs to someone else, or was retired. */
export function isIdentityTaken(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  const detail = error.detail
  return (
    !!detail &&
    typeof detail === 'object' &&
    (detail as { reason?: unknown }).reason === 'chat_identity_taken'
  )
}
